/**
 * src/db/backup.ts — Export/Import all, pre-migration backups, and quarantine access
 * (§9.2, §10.5). Since there is no central database, Export/Import is both the recovery
 * path and how users move data between browsers/devices.
 */
import type { Scenario, TaxProfile } from '@/types';
import { getDb, type QuarantineEntry } from './index';
import { migrateRecord } from './migrations';
import { getTaxProfile, setTaxProfile } from './appState';
import { saveScenario } from './scenarios';

export const EXPORT_APP_ID = 'sbloc-dashboard';

export interface ExportBundle {
  app: string;
  exportedAt: string;
  scenarios: Scenario[];
  taxProfile: TaxProfile | null;
}

export interface ImportResult {
  imported: number;
  /** Records that could not be migrated/validated (reported, not silently dropped). */
  skipped: number;
}

/** Snapshot all raw scenarios into the backups store before a bulk migration pass (§9.2). */
export async function backupAllScenarios(): Promise<string> {
  const db = await getDb();
  const scenarios = await db.getAll('scenarios');
  const key = new Date().toISOString();
  await db.put('backups', { key, createdAt: key, scenarios });
  return key;
}

/** Build the full export bundle (all scenarios + the tax profile). */
export async function exportAll(): Promise<ExportBundle> {
  const db = await getDb();
  const rawAll = await db.getAll('scenarios');
  const scenarios: Scenario[] = [];
  for (const raw of rawAll) {
    const res = migrateRecord(raw);
    if (res.status !== 'quarantine') scenarios.push(res.scenario);
  }
  const taxProfile = await getTaxProfile();
  return { app: EXPORT_APP_ID, exportedAt: new Date().toISOString(), scenarios, taxProfile };
}

/** Serialize the export bundle to a downloadable JSON string. */
export async function exportAllToJson(): Promise<string> {
  return JSON.stringify(await exportAll(), null, 2);
}

/** Import a bundle: migrate + validate each scenario; restore the tax profile (§10.5). */
export async function importAll(json: string): Promise<ImportResult> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return { imported: 0, skipped: 0 };
  }
  const bundle = parsed as Partial<ExportBundle>;
  const scenarios = Array.isArray(bundle.scenarios) ? bundle.scenarios : [];
  let imported = 0;
  let skipped = 0;
  for (const raw of scenarios) {
    const res = migrateRecord(raw);
    if (res.status === 'quarantine') {
      skipped++;
    } else {
      await saveScenario(res.scenario, false);
      imported++;
    }
  }
  if (bundle.taxProfile) await setTaxProfile(bundle.taxProfile);
  return { imported, skipped };
}

/** Restore the most recent backup's scenarios (§10.5 "restore from last backup"). */
export async function restoreLastBackup(): Promise<number> {
  const db = await getDb();
  const backups = await db.getAll('backups');
  if (backups.length === 0) return 0;
  backups.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const latest = backups[0]!;
  const tx = db.transaction('scenarios', 'readwrite');
  for (const raw of latest.scenarios) {
    await tx.store.put(raw as Record<string, unknown>);
  }
  await tx.done;
  return latest.scenarios.length;
}

export async function listQuarantine(): Promise<QuarantineEntry[]> {
  const db = await getDb();
  return db.getAll('quarantine');
}

export async function discardQuarantine(id: string): Promise<void> {
  const db = await getDb();
  await db.delete('quarantine', id);
}
